import { useEffect, useMemo } from 'react';
import { useInfiniteQuery, useQueryClient } from '../query';
import { useOperationalStatus } from '../hooks';
import { getQueryKey } from './utils';
import { useMessagesSync } from './useMessagesSync';
import { getConfig, isWidgetConfig } from '../config';
import { isUnauthorizedError } from '../axios';
const initialPageParam = '';
export const useMessagesQuery = (conversationId, options) => {
    const config = getConfig();
    const [operationalStatus] = useOperationalStatus();
    const queryKey = useMemo(() => getQueryKey(conversationId), [conversationId]);
    const messagesQuery = useInfiniteQuery({
        queryKey,
        queryFn: ({ pageParam }) => options.getMessages(conversationId, pageParam === initialPageParam ? undefined : pageParam),
        initialPageParam,
        getNextPageParam: (lastPage, pages) => {
            const pageBeforeLast = pages[pages.length - 2];
            const lastMessage = lastPage.messages[0];
            const lastMessageFromPageBeforeLast = pageBeforeLast?.messages[0];
            if (lastMessage?.id === lastMessageFromPageBeforeLast?.id) {
                return;
            }
            return lastMessage?.id;
        },
        enabled: options.enabled ||
            operationalStatus === 'degraded' ||
            (isWidgetConfig(config) && config.identityType === 'guest'),
        refetchInterval: query => {
            if (isUnauthorizedError(query.state.error)) {
                return false;
            }
            return operationalStatus === 'degraded' ||
                (isWidgetConfig(config) && config.identityType === 'guest')
                ? 5_000
                : false;
        },
    });
    useMessagesSync(conversationId, {
        syncMessages: options.syncMessages,
    });
    const queryClient = useQueryClient();
    useEffect(() => () => {
        queryClient.removeQueries({
            queryKey,
        });
    }, [queryClient, queryKey]);
    const [messages, users, contacts, guests] = useMemo(() => messagesQuery.data?.pages
        ? [
            [...new Set(messagesQuery.data.pages.flatMap(page => page.messages))],
            messagesQuery.data.pages.flatMap(page => page.users || []),
            messagesQuery.data.pages.flatMap(page => page.contacts || []),
            messagesQuery.data.pages.flatMap(page => page.guests || []),
        ]
        : [[], [], [], []], [messagesQuery.data?.pages]);
    return {
        messages,
        users,
        contacts,
        guests,
        isLoading: messagesQuery.isLoading,
        isFetching: messagesQuery.isFetching,
        error: messagesQuery.error,
        fetchNextPage: () => void messagesQuery.fetchNextPage(),
    };
};
//# sourceMappingURL=useMessagesQuery.js.map